<template>
    <div class="center-container-vertically">
        <form
            data-cy="email-address-form"
            onsubmit="return false"
            autocomplete="on"
        >
            <div class="input-container" v-if="!showOtpInput">
                <text-input
                    v-model="emailForm.emailAddress"
                    cypress-data="email-address-input"
                    container-style-class="medium-input-with-default-label"
                    input-style-class="medium-input"
                    label-style-class="medium-default-label"
                    label="labels.emailAddress"
                    placeholder="labels.empty"
                    name="email-address-input"
                    autocomplete-value="email"
                    input-type="email"
                />
                <div
                    class="error-container"
                    data-cy="email-address-error"
                    v-for="error in v$.emailAddress.$errors"
                    :key="error.$uid"
                >
                    <p>{{ error.$message }}</p>
                </div>
            </div>

            <div v-if="showOtpInput" class="input-container mb-2">
                <i18n-t
                    keypath="default.otpInfo"
                    tag="p"
                    scope="global"
                    class="center-align-text"
                >
                    <template #email>
                        <span class="bold-text">
                            {{ emailForm.emailAddress }}
                        </span>
                    </template>
                </i18n-t>
            </div>

            <div
                v-if="showOtpInput"
                class="input-container center-container-vertically"
            >
                <text-input
                    v-model="emailForm.emailCode"
                    cypress-data="email-code-input"
                    container-style-class="medium-input-with-default-label"
                    input-style-class="medium-input"
                    label-style-class="medium-default-label"
                    label="labels.enterCode"
                    placeholder="labels.empty"
                    name="email-code-input"
                    autocomplete-value="code"
                    input-type="text"
                />
            </div>
            <div class="buttons-container">
                <text-button
                    v-if="!showOtpInput"
                    type="submit"
                    btn-style="default-btn default-btn__medium default-btn__medium-width primary-btn align-item-center"
                    @run-method="sendCode()"
                    data-cy="login-with-code"
                    text-style="white-text"
                    text="labels.continue"
                    :btn-loading="loadSendCode"
                    :disabled="loadSendCode"
                />

                <text-button
                    v-if="showOtpInput"
                    btn-style="default-btn mt-2 default-btn__medium default-btn__medium-width primary-btn align-item-center"
                    @run-method="validateCode()"
                    data-cy="login-with-code"
                    text-style="white-text"
                    text="labels.continue"
                    :btn-loading="loadContinue"
                    :disabled="loadContinue"
                />

                <p v-if="showOtpInput">
                    {{ t("default.emailNotReceived") }}
                    <a class="primary-text bold-text" @click="resendCode()">{{
                        t("labels.resend")
                    }}</a>
                </p>

                <div
                    v-if="props.redirectTo !== routeNames.E_MANDATES_FORM"
                    class="or-separator"
                >
                    <div></div>
                    <p>{{ t("default.or") }}</p>
                    <div></div>
                </div>

                <left-inner-icon-text-button
                    v-if="props.showContinueWithoutLogin && !showOtpInput"
                    type="submit"
                    btn-style="inner-icon-btn inner-icon-btn__medium inner-icon-btn__medium-width contrast-primary-btn align-item-center"
                    @run-method="signInWithGoogle()"
                    data-cy="login-with-google"
                    text-style="alpha-dark-text"
                    text="labels.authenticateWithGoogle"
                    :icon-src="googleIconSvg"
                />

                <p
                    v-if="props.showContinueWithoutLogin"
                    class="mt-4 underline-text pointer-cursor"
                    @click="continueWithoutLogin"
                    data-cy="continue-without-logging-in"
                >
                    {{ t("labels.continueWithoutAuthentication") }}
                </p>
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted, ref } from "vue";
import useVuelidate from "@vuelidate/core";
import { usePiniaStore } from "@/plugins/store";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

import textInput from "@/components/base/textInput.vue";
import textButton from "@/components/base/textButton.vue";
import leftInnerIconTextButton from "@/components/base/leftInnerIconTextButton.vue";
import googleIconSvg from "@/assets/icons/20x20/ic20-google-colored.svg";

import { required, email } from "@/helpers/i18nValidation.ts";
import { getFormData } from "@/apis/esendaV2/organisations";
import { showAlert } from "@/helpers/errorHandling";
import { setSentryUser } from "@/helpers/sentry";
import {
    signIn,
    signUp,
    confirmSignUp,
    autoSignIn,
    confirmSignIn,
    resendSignUpCode,
    signInWithRedirect,
} from "aws-amplify/auth";

import { configSentryError } from "@/helpers/sentry";

import { routeNames } from "@/constants/routeNames";

const { t } = useI18n();
const pStore = usePiniaStore();
const router = useRouter();
const route = useRoute();

const props = defineProps<{
    showContinueWithoutLogin: boolean;
    skipGetForm: boolean;
    redirectTo: routeNames;
}>();

const shortcode = computed(() => route.params.shortcode);
const formName = computed(() =>
    route.params.form ? route.params.form : "invoice",
);
const accountAlias = computed(() =>
    route.params.accountAlias ? String(route.params.accountAlias) : null,
);

/** Input values */
const emailForm = reactive({
    emailAddress: "",
    emailCode: "",
});

/** Validation config */
const rules = computed(() => {
    return {
        emailAddress: { required, email, $autoDirty: true },
    };
});
const v$ = useVuelidate(rules, emailForm);

const continueWithoutLogin = async () => {
    const result = await v$.value.$validate();
    if (result) {
        if (pStore.isAuthenticated) {
            handleAuthenticatedUsers();
            return;
        }

        pStore.setEmailAddress(emailForm.emailAddress);
        setSentryUser(emailForm.emailAddress, String(pStore.getSessionId));

        router.push({
            name: routeNames.SUPPLEMENTARY_FORM,
            params: {
                shortcode: shortcode.value,
                form: formName.value,
            },
        });
    } else {
        showAlert("alerts.validationError", true);
    }
};

const showOtpInput = ref(false);
const existingUser = ref(false);
const loadSendCode = ref(false);
const loadContinue = ref(false);

const handleSignUp = async () => {
    /**
     * Creates a new user with and sends a verification email to their email address.
     */
    const { nextStep: signInNextStep } = await signUp({
        username: String(pStore.getEmailAddress),
        options: {
            userAttributes: {
                email: String(pStore.getEmailAddress),
            },
            autoSignIn: {
                authFlowType: "USER_AUTH",
            },
        },
    });

    if (signInNextStep.signUpStep === "CONFIRM_SIGN_UP") {
        showOtpInput.value = true;
    } else {
        showAlert("alerts.unexpectedErrorWithRetryAction", true);
        configSentryError(
            "login",
            "handleSignUp",
            `Sign up next step is not CONFIRM_SIGN_UP. Actual value: ${JSON.stringify(signInNextStep, null, 2)}`,
        );
    }

    loadSendCode.value = false;
};

const handleAuthenticatedUsers = () => {
    /**
     * If the user is already logged in, redirect to the corresponding page
     * on "Continue" or "Authenticate with Google click" click
     */
    if (emailForm.emailAddress !== pStore.getEmailAddress) {
        // If the user is already logged in, but entered a different email address show warning
        showAlert("alerts.loginWithDifferentEmail", true);
        loadSendCode.value = false;
        return;
    }
    postLogInRedirect();
};

const sendCode = async () => {
    /**
     * Validates the email address and attempts to sign in the user by sending
     * an OTP code to their email address.
     * If the user does not exist, it automatically creates one and sends an OTP
     * to their email address.
     */
    const result = await v$.value.$validate();
    loadSendCode.value = true;

    if (result) {
        if (pStore.isAuthenticated) {
            handleAuthenticatedUsers();
            return;
        }

        pStore.setEmailAddress(emailForm.emailAddress);
        setSentryUser(emailForm.emailAddress, String(pStore.getSessionId));

        try {
            await signIn({
                username: String(pStore.getEmailAddress),
                options: {
                    authFlowType: "USER_AUTH",
                    preferredChallenge: "EMAIL_OTP",
                },
            });
            existingUser.value = true;
            showOtpInput.value = true;
        } catch (error) {
            if (String(error).includes("UserNotFoundException")) {
                existingUser.value = false;
                handleSignUp();
            } else {
                loadSendCode.value = false;
                showAlert("alerts.unexpectedErrorWithRetryAction", true);
                configSentryError(
                    "login",
                    "sendCode",
                    `Error while trying to sign in the user: ${error}`,
                );
            }
        }
    } else {
        loadSendCode.value = false;
        showAlert("alerts.validationError", true);
    }
};

const postLogInRedirect = async () => {
    /** Redirect the user to the correct router after log in */
    const routeName =
        props.redirectTo === routeNames.SUPPLEMENTARY_FORM
            ? routeNames.SUPPLEMENTARY_FORM
            : routeNames.E_MANDATES_FORM;

    const params = {
        shortcode: shortcode.value,
        ...(props.redirectTo === routeNames.SUPPLEMENTARY_FORM
            ? { form: formName.value }
            : { accountAlias: accountAlias.value }),
    };

    if (
        props.redirectTo === routeNames.E_MANDATES_FORM &&
        !accountAlias.value
    ) {
        loadSendCode.value = false;
        showAlert("alerts.incorrectEmandatesUrl", true);
        return;
    }

    router.push({ name: routeName, params }).catch((error) => {
        configSentryError(
            "login",
            "postLogInRedirect",
            `Error redirecting users after login: ${error}`,
        );
    });
};

const handleNonExistentUser = async () => {
    /**
     * Validate the OTP code for a non existent user.
     */
    try {
        const { nextStep: confirmSignUpNextStep } = await confirmSignUp({
            username: emailForm.emailAddress,
            confirmationCode: emailForm.emailCode,
        });

        if (confirmSignUpNextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
            const { nextStep } = await autoSignIn();

            if (nextStep.signInStep === "DONE") {
                loadContinue.value = false;
                postLogInRedirect();
            }
        } else {
            loadContinue.value = false;
            showAlert("alerts.unexpectedErrorWithRetryAction", true);
            configSentryError(
                "login",
                "handleNonExistentUser",
                `Confirm sign up next step is not COMPLETE_AUTO_SIGN_IN. Actual value: ${JSON.stringify(confirmSignUpNextStep, null, 2)}`,
            );
        }
    } catch (error) {
        if (String(error).includes("CodeMismatchException")) {
            loadContinue.value = false;
            showAlert("alerts.incorrectOtp", true);
        } else {
            loadContinue.value = false;
            showAlert("alerts.unexpectedErrorWithRetryAction", true);
            configSentryError(
                "login",
                "handleNonExistentUser",
                `Something went wrong while validating the sign up OTP: ${JSON.stringify(error, null, 2)}`,
            );
        }
    }
};

const handleExistingUser = async () => {
    /**
     * Validate the OTP code for an existent user.
     */
    try {
        const { nextStep: confirmSignInNextStep } = await confirmSignIn({
            challengeResponse: emailForm.emailCode,
        });

        if (confirmSignInNextStep.signInStep === "DONE") {
            loadContinue.value = false;
            postLogInRedirect();
        } else {
            loadContinue.value = false;
            showAlert("alerts.unexpectedErrorWithRetryAction", true);
            configSentryError(
                "login",
                "handleExistingUser",
                `Confirm sign in next step is not DONE. Actual value: ${JSON.stringify(confirmSignInNextStep, null, 2)}`,
            );
        }
    } catch (error) {
        if (String(error).includes("CodeMismatchException")) {
            loadContinue.value = false;
            showAlert("alerts.incorrectOtp", true);
        } else {
            loadContinue.value = false;
            showAlert("alerts.unexpectedErrorWithRetryAction", true);
            configSentryError(
                "login",
                "handleExistingUser",
                `Something went wrong while validating the sign in OTP: ${JSON.stringify(error, null, 2)}`,
            );
        }
    }
};

const validateCode = async () => {
    /** Trigger the correct OTP validation */
    if (pStore.isAuthenticated) {
        handleAuthenticatedUsers();
        return;
    }

    if (!emailForm.emailCode) {
        showAlert("alerts.validationError", true);
        return;
    }
    loadContinue.value = true;
    existingUser.value ? handleExistingUser() : handleNonExistentUser();
};

const resendCode = async () => {
    /**
     * If the user already exists, retry the send code option.
     * Otherwise, use the amplify resendSignUpCode method.
     */
    existingUser.value
        ? await sendCode().then(() => {
              showAlert("alerts.resendCode", true, "success");
          })
        : await resendSignUpCode({
              username: emailForm.emailAddress,
          }).then(() => {
              showAlert("alerts.resendCode", true, "success");
          });
};

const signInWithGoogle = async () => {
    /**
     * Validates the entered email address and attempts to sign in the user
     * using Google as the authentication provider
     */
    const result = await v$.value.$validate();
    if (result) {
        pStore.setEmailAddress(emailForm.emailAddress);
        setSentryUser(emailForm.emailAddress, String(pStore.getSessionId));

        if (pStore.isAuthenticated) {
            /**
             * If the user is already logged in, redirect to the corresponding page
             * on "Continue" or "Authenticate with Google click" click
             */
            postLogInRedirect();
            return;
        }

        try {
            await signInWithRedirect({
                provider: "Google",
            });
        } catch (error) {
            showAlert("alerts.unexpectedErrorWithRetryAction", true);
            configSentryError(
                "login",
                "signInWithGoogle",
                `Error while trying to sign in the user using Google as the provider: ${error}`,
            );
        }
    } else {
        showAlert("alerts.validationError", true);
    }
};

const getForm = async () => {
    /** Exit get form if the prop is set to true, otherwise call the API */
    if (props.skipGetForm) {
        return;
    }

    await getFormData(String(shortcode.value), String(formName.value));
};

onMounted(() => {
    /** Pre-fill email address input if it exits in the session storage */
    const savedEmailAddress =
        pStore.getEmailAddress !== null ? pStore.getEmailAddress : "";
    emailForm.emailAddress = savedEmailAddress;

    // Set the origin route
    props.redirectTo === routeNames.E_MANDATES_FORM
        ? pStore.setOrigin(routeNames.E_MANDATES_LANDING_PAGE)
        : pStore.setOrigin(routeNames.LOGIN_PAGE);
});

getForm();
</script>
